import React, { useState, useEffect, useMemo } from 'react';
import config from '../../config';
import axios from 'axios';
import { toast } from 'react-toastify';

// MUI Components
import {
  Container,
  Card,
  CardContent,
  Typography,
  TextField,
  MenuItem,
  Button,
  Box,
  Divider,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  CircularProgress,
  IconButton,
  Avatar,
} from '@mui/material';

import FileDownloadIcon from '@mui/icons-material/FileDownload';
import FileUploadIcon from '@mui/icons-material/FileUpload';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DeleteIcon from '@mui/icons-material/Delete';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';

export default function AddAndManageCategories() {
  const company = JSON.parse(sessionStorage.getItem('companyDetails') || '{}');
  const token = sessionStorage.getItem('token');

  // ==================== ADD FORM STATES ====================
  const [categoryName, setCategoryName] = useState('');
  const [categoryImage, setCategoryImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  // Department this category belongs to (optional — backward compatible).
  const [departments, setDepartments] = useState([]);
  const [selectedDepartment, setSelectedDepartment] = useState('');

  // ==================== TABLE STATES ====================
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const [editOpen, setEditOpen] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [saving, setSaving] = useState(false);

  // ==================== EXCEL IMPORT STATES ====================
  const [importOpen, setImportOpen] = useState(false);
  const [importing, setImporting] = useState(false);
  const [importProgress, setImportProgress] = useState({ done: 0, total: 0 });
  const [importReport, setImportReport] = useState({
    success: [],
    skipped: [],
    failed: [],
  });

  // ==================== FETCH CATEGORIES ====================
  const fetchCategories = async () => {
    if (!company?.id || !token) {
      setCategories([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      const res = await axios.get(
        `${config.API_BASE_URL}/home/GetCategory/${company.id}`,
        { headers: { Authorization: `Bearer ${token}` } },
      );

      let list = [];
      const data = res.data;

      if (Array.isArray(data)) list = data;
      else if (data?.data) list = data.data;
      else if (data?.categories) list = data.categories;
      else if (data?.result) list = data.result;
      else list = [];

      setCategories(list);
    } catch (err) {
      console.error('Failed to fetch categories:', err);
      setCategories([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchDepartments = async () => {
    if (!company?.id || !token) return;
    try {
      const res = await axios.get(
        `${config.API_BASE_URL}/Home/GetDepartments/${company.id}`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setDepartments(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error('Failed to fetch departments:', err);
      setDepartments([]);
    }
  };

  useEffect(() => {
    fetchCategories();
    fetchDepartments();
  }, []);

  // ==================== IMAGE HANDLING ====================
  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = [
      'image/jpeg',
      'image/jpg',
      'image/png',
      'image/gif',
      'image/webp',
    ];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Invalid file type. Please upload jpg, jpeg, png, gif, or webp.');
      return;
    }

    // Validate file size (5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image size must be less than 5MB');
      return;
    }

    setCategoryImage(file);

    // Generate preview
    const reader = new FileReader();
    reader.onloadend = () => setImagePreview(reader.result);
    reader.readAsDataURL(file);
  };

  const removeImage = () => {
    setCategoryImage(null);
    setImagePreview(null);
    // Reset the file input
    const fileInput = document.getElementById('category-image-input');
    if (fileInput) fileInput.value = '';
  };

  // ==================== SAVE CATEGORY ====================
  const saveCategory = async () => {
    if (!categoryName.trim()) {
      toast.error('Category Name is required');
      return;
    }

    // Use FormData for multipart/form-data
    const formData = new FormData();
    formData.append('CategoryName', categoryName.trim());
    formData.append('CompanyId', company?.id);
    if (selectedDepartment) {
      formData.append('DepartmentId', selectedDepartment);
    }
    if (categoryImage) {
      formData.append('CategoryImage', categoryImage);
    }

    try {
      setSaving(true);
      await axios.post(`${config.API_BASE_URL}/Home/CreateCategory`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data',
        },
      });

      toast.success('Category created successfully!');
      setCategoryName('');
      setSelectedDepartment('');
      removeImage();
      fetchCategories(); // Refresh list
    } catch (err) {
      console.error(err);
      toast.error(err.response?.data?.message || 'Failed to create category');
    } finally {
      setSaving(false);
    }
  };

  // ==================== TABLE LOGIC ====================
  const filteredCategories = useMemo(() => {
    const searchLower = search.toLowerCase().trim();
    return categories.filter((cat) =>
      cat?.category_name?.toLowerCase().includes(searchLower),
    );
  }, [categories, search]);

  const displayedCategories = useMemo(() => {
    const start = page * rowsPerPage;
    return filteredCategories.slice(start, start + rowsPerPage);
  }, [filteredCategories, page, rowsPerPage]);

  const exportToExcel = () => {
    if (filteredCategories.length === 0) {
      toast.error('No categories to export');
      return;
    }

    const formatted = filteredCategories.map((c, index) => ({
      '#': index + 1,
      Category: c.category_name || '',
      Status: c.isActive ? 'Active' : 'Inactive',
      'Created Date': c.createdate || '-',
    }));

    const ws = XLSX.utils.json_to_sheet(formatted);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Categories');
    XLSX.writeFile(wb, 'CategoryReport.xlsx');
  };

  // ==================== EXCEL IMPORT ====================
  // Template has a single required column `CategoryName`. Empty rows + blank
  // names are dropped; existing names (case-insensitive) get reported as
  // "Skipped (Duplicate)" instead of hitting the API.
  const downloadCategoryTemplate = () => {
    const sample = [
      { CategoryName: 'Beverages' },
      { CategoryName: 'Starters' },
      { CategoryName: 'Main Course' },
    ];
    const ws = XLSX.utils.json_to_sheet(sample);
    ws['!cols'] = [{ wch: 30 }];
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Categories');
    const buf = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
    saveAs(
      new Blob([buf], { type: 'application/octet-stream' }),
      'Category_Import_Template.xlsx',
    );
  };

  const handleImportExcel = (e) => {
    const file = e.target.files?.[0];
    e.target.value = ''; // allow re-importing the same file later
    if (!file) return;

    const reader = new FileReader();
    reader.onload = async (ev) => {
      try {
        const wb = XLSX.read(ev.target.result, { type: 'array' });
        const sheet = wb.Sheets[wb.SheetNames[0]];
        const rows = XLSX.utils.sheet_to_json(sheet, { defval: '' });

        // Normalise — accept "CategoryName" or "Category Name" or "Category"
        const cleaned = rows
          .map((r) => {
            const name = String(
              r.CategoryName ??
                r['Category Name'] ??
                r.Category ??
                r.category_name ??
                '',
            ).trim();
            return name;
          })
          .filter((n) => n.length > 0);

        if (cleaned.length === 0) {
          toast.error(
            'No valid rows found. Make sure your Excel has a "CategoryName" column.',
          );
          return;
        }

        // Build existing-name set so we can short-circuit duplicates client-side
        // before hitting the API. Server may still reject extras — we capture
        // those in `failed`.
        const existingLower = new Set(
          categories.map((c) =>
            (c.category_name || '').trim().toLowerCase(),
          ),
        );

        setImporting(true);
        setImportProgress({ done: 0, total: cleaned.length });
        const report = { success: [], skipped: [], failed: [] };

        for (let i = 0; i < cleaned.length; i++) {
          const name = cleaned[i];
          if (existingLower.has(name.toLowerCase())) {
            report.skipped.push({ name, reason: 'Already exists' });
            setImportProgress({ done: i + 1, total: cleaned.length });
            continue;
          }

          try {
            const fd = new FormData();
            fd.append('CategoryName', name);
            fd.append('CompanyId', company?.id);
            await axios.post(
              `${config.API_BASE_URL}/Home/CreateCategory`,
              fd,
              {
                headers: {
                  Authorization: `Bearer ${token}`,
                  'Content-Type': 'multipart/form-data',
                },
              },
            );
            report.success.push({ name });
            existingLower.add(name.toLowerCase()); // avoid intra-file duplicates
          } catch (err) {
            report.failed.push({
              name,
              reason:
                err.response?.data?.message ||
                err.message ||
                'API error',
            });
          }
          setImportProgress({ done: i + 1, total: cleaned.length });
        }

        setImportReport(report);
        await fetchCategories();
      } catch (err) {
        console.error('Excel parse failed:', err);
        toast.error(
          'Failed to read Excel file. Please use the provided template format.',
        );
      } finally {
        setImporting(false);
      }
    };
    reader.readAsArrayBuffer(file);
  };

  // ==================== ACTIONS ====================
  const toggleStatus = async (id) => {
    if (!id) return;
    try {
      await axios.get(`${config.API_BASE_URL}/home/DeactivateCategory/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      fetchCategories();
    } catch (err) {
      console.error(err);
    }
  };

  const openEdit = (item) => {
    setEditItem({ ...item });
    setEditOpen(true);
  };

  const updateCategory = async () => {
    if (!editItem?.id || !editItem?.category_name?.trim()) {
      toast.error('Category name is required');
      return;
    }

    try {
      await axios.get(
        `${config.API_BASE_URL}/home/UpdateCategory/${encodeURIComponent(
          editItem.category_name.trim(),
        )}/${editItem.id}`,
        { headers: { Authorization: `Bearer ${token}` } },
      );

      setEditOpen(false);
      fetchCategories();
    } catch (err) {
      console.error(err);
      toast.error('Failed to update category');
    }
  };

  // Helper to build full image URL
  const getImageUrl = (path) => {
    if (!path) return null;
    if (path.startsWith('http')) return path;
    return `${config.API_BASE_URL.replace('/api', '')}${path}`;
  };

  if (loading) {
    return (
      <Box display='flex' justifyContent='center' mt={10}>
        <CircularProgress size={60} />
      </Box>
    );
  }

  return (
    <Container maxWidth='lg' sx={{ mt: 4, mb: 6 }}>
      {/* ==================== ADD CATEGORY FORM ==================== */}
      <Card sx={{ borderRadius: 4, boxShadow: 4, mb: 6 }}>
        <CardContent>
          <Typography
            variant='h5'
            align='center'
            sx={{ mb: 3, fontWeight: 700, color: '#1976d2' }}>
            Create New Category
          </Typography>
          <Divider sx={{ mb: 3 }} />

          <Box display='flex' flexDirection='column' gap={2}>
            <TextField
              label='Category Name'
              fullWidth
              value={categoryName}
              onChange={(e) => setCategoryName(e.target.value)}
              required
            />

            {/* Department this category belongs to (optional). */}
            <TextField
              select
              label='Department'
              fullWidth
              value={selectedDepartment}
              onChange={(e) => setSelectedDepartment(e.target.value)}
              helperText='Which department (Kitchen / Bar / Bakery...) this category belongs to'>
              <MenuItem value=''>— None —</MenuItem>
              {departments.map((d) => (
                <MenuItem key={d.id ?? d.Id} value={d.id ?? d.Id}>
                  {d.department || d.Department}
                  {d.Printername || d.printername
                    ? ` — ${d.Printername || d.printername}`
                    : ''}
                </MenuItem>
              ))}
            </TextField>

            {/* Image Upload Section */}
            <Box>
              <input
                id='category-image-input'
                type='file'
                accept='image/jpeg,image/jpg,image/png,image/gif,image/webp'
                style={{ display: 'none' }}
                onChange={handleImageChange}
              />

              <Box display='flex' alignItems='center' gap={2} flexWrap='wrap'>
                <label htmlFor='category-image-input'>
                  <Button
                    variant='outlined'
                    component='span'
                    startIcon={<CloudUploadIcon />}>
                    {categoryImage ? 'Change Image' : 'Upload Image'}
                  </Button>
                </label>

                {categoryImage && (
                  <>
                    <Typography variant='body2' color='text.secondary'>
                      {categoryImage.name} (
                      {(categoryImage.size / 1024).toFixed(1)} KB)
                    </Typography>
                    <IconButton
                      size='small'
                      color='error'
                      onClick={removeImage}>
                      <DeleteIcon fontSize='small' />
                    </IconButton>
                  </>
                )}
              </Box>

              {imagePreview && (
                <Box mt={2}>
                  <img
                    src={imagePreview}
                    alt='Preview'
                    style={{
                      maxWidth: '200px',
                      maxHeight: '200px',
                      borderRadius: '8px',
                      border: '1px solid #ddd',
                      objectFit: 'cover',
                    }}
                  />
                </Box>
              )}

              <Typography
                variant='caption'
                color='text.secondary'
                display='block'
                mt={1}>
                Allowed: JPG, PNG, GIF, WEBP. Max size: 5MB
              </Typography>
            </Box>

            <Button
              variant='contained'
              size='large'
              onClick={saveCategory}
              disabled={saving}
              sx={{ mt: 2, py: 1.5 }}>
              {saving ? (
                <CircularProgress size={24} color='inherit' />
              ) : (
                'Save Category'
              )}
            </Button>
          </Box>
        </CardContent>
      </Card>

      {/* ==================== CATEGORY REPORT ==================== */}
      <Card sx={{ borderRadius: 4, boxShadow: 3 }}>
        <CardContent sx={{ pb: 1 }}>
          <Typography variant='h6' fontWeight={600} sx={{ mb: 3 }}>
            All Categories
          </Typography>

          {/* Filters */}
          <Box
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: 2,
              mb: 3,
              alignItems: 'center',
            }}>
            <TextField
              label='Search Category'
              size='small'
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(0);
              }}
              sx={{ minWidth: 280 }}
            />

            <Box sx={{ ml: 'auto', display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              <Button
                variant='outlined'
                color='info'
                startIcon={<FileDownloadIcon />}
                onClick={downloadCategoryTemplate}>
                Download Template
              </Button>
              <Button
                variant='contained'
                color='success'
                component='label'
                startIcon={<FileUploadIcon />}
                disabled={importing}>
                {importing ? 'Importing...' : 'Import Excel'}
                <input
                  type='file'
                  hidden
                  accept='.xlsx, .xls, .csv'
                  onChange={handleImportExcel}
                />
              </Button>
              <Button
                variant='outlined'
                startIcon={<FileDownloadIcon />}
                onClick={exportToExcel}
                disabled={filteredCategories.length === 0}>
                Export Excel
              </Button>
            </Box>
          </Box>

          {/* Table */}
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow sx={{ backgroundColor: '#f8fafc' }}>
                  <TableCell width={80}>#</TableCell>
                  <TableCell width={80}>Image</TableCell>
                  <TableCell>Category Name</TableCell>
                  <TableCell width={140}>Status</TableCell>
                  <TableCell width={220}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {displayedCategories.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} align='center' sx={{ py: 8 }}>
                      <Typography color='text.secondary'>
                        {search.trim()
                          ? 'No categories match your search'
                          : 'No categories found'}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  displayedCategories.map((cat, index) => (
                    <TableRow key={cat.id} hover>
                      <TableCell>{page * rowsPerPage + index + 1}</TableCell>
                      <TableCell>
                        <Avatar
                          src={getImageUrl(
                            cat.categoryImage || cat.category_image,
                          )}
                          alt={cat.category_name}
                          variant='rounded'
                          sx={{ width: 48, height: 48 }}>
                          {cat.category_name?.[0]?.toUpperCase() || '?'}
                        </Avatar>
                      </TableCell>
                      <TableCell>{cat.category_name || '—'}</TableCell>
                      <TableCell>
                        <Chip
                          label={cat.isActive ? 'Active' : 'Inactive'}
                          color={cat.isActive ? 'success' : 'error'}
                          size='small'
                        />
                      </TableCell>
                      <TableCell>
                        <Button
                          size='small'
                          variant='contained'
                          color={cat.isActive ? 'warning' : 'success'}
                          onClick={() => toggleStatus(cat.id)}
                          sx={{ mr: 1, minWidth: 100 }}>
                          {cat.isActive ? 'Deactivate' : 'Activate'}
                        </Button>

                        <Button
                          size='small'
                          variant='contained'
                          onClick={() => openEdit(cat)}>
                          Update
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>

          <TablePagination
            rowsPerPageOptions={[5, 10, 25, 50]}
            component='div'
            count={filteredCategories.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={(_, newPage) => setPage(newPage)}
            onRowsPerPageChange={(e) => {
              setRowsPerPage(parseInt(e.target.value, 10));
              setPage(0);
            }}
          />
        </CardContent>
      </Card>

      {/* ==================== EDIT DIALOG ==================== */}
      <Dialog
        open={editOpen}
        onClose={() => setEditOpen(false)}
        maxWidth='sm'
        fullWidth>
        <DialogTitle>Update Category</DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <TextField
            autoFocus
            margin='dense'
            label='Category Name'
            fullWidth
            variant='outlined'
            value={editItem?.category_name || ''}
            onChange={(e) =>
              setEditItem((prev) => ({
                ...prev,
                category_name: e.target.value,
              }))
            }
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditOpen(false)}>Cancel</Button>
          <Button
            variant='contained'
            onClick={updateCategory}
            disabled={!editItem?.category_name?.trim()}>
            Save Changes
          </Button>
        </DialogActions>
      </Dialog>

      {/* ==================== IMPORT PROGRESS / REPORT DIALOG ==================== */}
      <Dialog
        open={importing || importReport.success.length + importReport.skipped.length + importReport.failed.length > 0}
        onClose={() => {
          if (importing) return;
          setImportReport({ success: [], skipped: [], failed: [] });
        }}
        maxWidth='sm'
        fullWidth>
        <DialogTitle sx={{ bgcolor: '#1976d2', color: '#fff', fontWeight: 700 }}>
          {importing ? 'Importing Categories...' : 'Import Report'}
        </DialogTitle>
        <DialogContent dividers>
          {importing ? (
            <Box sx={{ py: 3, textAlign: 'center' }}>
              <CircularProgress sx={{ mb: 2 }} />
              <Typography>
                Processing {importProgress.done} of {importProgress.total}...
              </Typography>
            </Box>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                <Chip
                  label={`Added: ${importReport.success.length}`}
                  color='success'
                />
                <Chip
                  label={`Skipped: ${importReport.skipped.length}`}
                  color='warning'
                />
                <Chip
                  label={`Failed: ${importReport.failed.length}`}
                  color='error'
                />
              </Box>
              {importReport.failed.length > 0 && (
                <Box>
                  <Typography variant='subtitle2' color='error' gutterBottom>
                    Failed rows
                  </Typography>
                  {importReport.failed.map((r, i) => (
                    <Typography
                      key={`f-${i}`}
                      variant='caption'
                      display='block'
                      sx={{ pl: 1 }}>
                      • {r.name} — {r.reason}
                    </Typography>
                  ))}
                </Box>
              )}
              {importReport.skipped.length > 0 && (
                <Box>
                  <Typography variant='subtitle2' color='warning.main' gutterBottom>
                    Skipped (duplicates)
                  </Typography>
                  {importReport.skipped.map((r, i) => (
                    <Typography
                      key={`s-${i}`}
                      variant='caption'
                      display='block'
                      sx={{ pl: 1 }}>
                      • {r.name}
                    </Typography>
                  ))}
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() =>
              setImportReport({ success: [], skipped: [], failed: [] })
            }
            disabled={importing}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}
